import { createElement, Fragment, isValidElement } from 'react';

const STORAGE_KEY = 'ctmass.language';
const CHANGED_KEY = 'ctmass.language.changedFrom';
const SUPPORTED = ['en', 'es'];
const PLACEHOLDER = /\{\{(\d+)\}\}/g;

const readStorage = (getStorage, key) => {
  try {
    return getStorage().getItem(key);
  } catch (err) {
    return null;
  }
};

const writeStorage = (getStorage, key, value) => {
  try {
    getStorage().setItem(key, value);
  } catch (err) {
    console.warn(err);
  }
};

const local = () => window.localStorage;
const session = () => window.sessionStorage;

const detectBrowserLanguage = () => {
  const locales = navigator.languages?.length ? navigator.languages : [navigator.language];
  for (const locale of locales) {
    const code = String(locale || '').split('-')[0].toLowerCase();
    if (SUPPORTED.includes(code)) return code;
  }
  return 'en';
};

const stored = readStorage(local, STORAGE_KEY);

export const language = SUPPORTED.includes(stored) ? stored : detectBrowserLanguage();
export const languageSource = SUPPORTED.includes(stored) ? 'manual' : 'browser';

let dictionary = null;

export const loadDictionary = async () => {
  document.documentElement.lang = language;
  if (language !== 'es') return;
  const [{ esWeb }, { default: esLocale }, { default: setDefaultOptions }] = await Promise.all([
    import('./translations/es-web'),
    import('date-fns/locale/es'),
    import('date-fns/setDefaultOptions')
  ]);
  dictionary = esWeb;
  setDefaultOptions({ locale: esLocale });
};

export const changeLanguage = (next) => {
  if (next === language) return;
  writeStorage(session, CHANGED_KEY, language);
  writeStorage(local, STORAGE_KEY, next);
  window.location.reload();
};

export const consumeLanguageChange = () => {
  const previous = readStorage(session, CHANGED_KEY);
  if (previous) {
    try {
      session().removeItem(CHANGED_KEY);
    } catch (err) {
      console.warn(err);
    }
  }
  return previous;
};

const isRenderable = (value) => isValidElement(value) || Array.isArray(value);

export const tr = (key, params) => {
  if (typeof key !== 'string') return key;
  const template = dictionary?.[key] ?? key;
  if (!params) return template;

  const hasElements = Object.values(params).some(isRenderable);
  if (!hasElements) {
    return template.replace(PLACEHOLDER, (match, index) => {
      const value = params[index];
      return value === null || value === undefined || value === false ? '' : String(value);
    });
  }

  const parts = template.split(PLACEHOLDER).map((part, index) => (index % 2 ? params[part] : part));
  return createElement(Fragment, null, ...parts);
};
